import React, { useState, useEffect, useMemo } from 'react';
import { reportsAPI } from '../utils/api';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { Pagination, Badge } from 'react-bootstrap';
import '../css/OverallReport.css';

const MonthWiseBalanceReport = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statistics, setStatistics] = useState({
        totalCustomerBalance: 0,
        totalVendorBalance: 0
    });
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: 25,
        totalPages: 0
    });
    const [filters, setFilters] = useState({
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        clientType: 'All',
        searchQuery: ''
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const months = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    const years = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const yearsArray = [];
        for (let i = currentYear; i >= currentYear - 5; i--) {
            yearsArray.push(i);
        }
        return yearsArray;
    }, []);

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.searchQuery);
        }, 500);
        return () => clearTimeout(timer);
    }, [filters.searchQuery]);

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filters.month, filters.year, filters.clientType, debouncedSearch]);

    const fetchBalances = async () => {
        try {
            setLoading(true);
            const params = {
                month: filters.month,
                year: filters.year,
                searchQuery: debouncedSearch,
                clientType: filters.clientType,
                page: currentPage,
                limit: 25
            };
            const response = await reportsAPI.getBalanceSummary(params);
            if (response.data.success) {
                setData(response.data.data);
                setStatistics(response.data.statistics);
                setPagination(response.data.pagination);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error details:', err);
            setError('Failed to fetch balance summary. Please try again.');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBalances();
    }, [filters.month, filters.year, filters.clientType, debouncedSearch, currentPage]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo(0, 0);
    };

    const exportToPDF = async () => {
        try {
            setLoading(true);
            const params = {
                month: filters.month,
                year: filters.year,
                searchQuery: debouncedSearch,
                clientType: filters.clientType,
                exportAll: true
            };
            const response = await reportsAPI.getBalanceSummary(params);
            
            if (!response.data.success) {
                throw new Error("Failed to fetch full data for export");
            }

            const allData = response.data.data;
            const doc = new jsPDF();
            const tableColumn = ["Sl No", "Client Name", "Type", "Closing Balance"];
            const tableRows = [];

            allData.forEach((item, index) => {
                const rowData = [
                    index + 1,
                    item.name,
                    item.type,
                    `₹${item.balance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
                ];
                tableRows.push(rowData);
            });

            const monthLabel = months.find(m => m.value === parseInt(filters.month))?.label;

            doc.setFontSize(18);
            doc.text("Month-wise Balance Report", 14, 15);
            doc.setFontSize(11);
            doc.text(`Period: ${monthLabel} ${filters.year}`, 14, 22);
            doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 29);
            doc.text(`Total Records: ${allData.length}`, 14, 34);

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: 40,
                theme: 'grid',
                headStyles: { fillColor: [41, 128, 185], textColor: 255 },
                alternateRowStyles: { fillColor: [245, 245, 245] },
            });

            doc.save(`Balance_Report_${monthLabel}_${filters.year}.pdf`);
            setLoading(false);
        } catch (err) {
            console.error('Export error:', err);
            setError('Failed to export PDF. Please try again.');
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
        }).format(amount);
    };

    const renderPagination = () => {
        if (pagination.totalPages <= 1) return null;

        let items = [];
        const { totalPages, page } = pagination;

        let startPage = Math.max(1, page - 2);
        let endPage = Math.min(totalPages, startPage + 4);

        if (endPage - startPage < 4) {
            startPage = Math.max(1, endPage - 4);
        }
        startPage = Math.max(1, startPage);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === page}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        return (
            <Pagination className="justify-content-center mt-4">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={page === 1} />
                <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 1} />
                {startPage > 1 && <Pagination.Ellipsis disabled />}
                {items}
                {endPage < totalPages && <Pagination.Ellipsis disabled />}
                <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages} />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={page === totalPages} />
            </Pagination>
        );
    };

    return (
        <div className="report-container p-4">
            <div className="report-header d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold text-primary mb-1">Monthly Balance Report</h2>
                    <p className="text-muted">Detailed list of balances for all customers and vendors</p>
                </div>
                <button
                    className="btn btn-primary d-flex align-items-center"
                    onClick={exportToPDF}
                    disabled={loading || data.length === 0}
                >
                    <i className="bi bi-file-earmark-pdf me-2"></i>
                    Export PDF
                </button>
            </div>

            {/* Summary Cards */}
            <div className="row mb-4">
                <div className="col-md-6">
                    <div className="card shadow-sm border-0 border-start border-primary border-4">
                        <div className="card-body">
                            <h6 className="text-muted text-uppercase small fw-bold">Total Customer Balance</h6>
                            <h3 className="mb-0 text-primary">{formatCurrency(statistics.totalCustomerBalance)}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card shadow-sm border-0 border-start border-success border-4">
                        <div className="card-body">
                            <h6 className="text-muted text-uppercase small fw-bold">Total Vendor Balance</h6>
                            <h3 className="mb-0 text-success">{formatCurrency(statistics.totalVendorBalance)}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label className="form-label small fw-bold">Month</label>
                            <select
                                className="form-select"
                                value={filters.month}
                                onChange={(e) => setFilters({ ...filters, month: parseInt(e.target.value) })}
                            >
                                {months.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label small fw-bold">Year</label>
                            <select
                                className="form-select"
                                value={filters.year}
                                onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
                            >
                                {years.map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label small fw-bold">Client Type</label>
                            <select
                                className="form-select"
                                value={filters.clientType}
                                onChange={(e) => setFilters({ ...filters, clientType: e.target.value })}
                            >
                                <option value="All">All Clients</option>
                                <option value="Customer">Customers Only</option>
                                <option value="Vendor">Vendors Only</option>
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label small fw-bold">Search Client</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control border-start-0"
                                    placeholder="Search by name..."
                                    value={filters.searchQuery}
                                    onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card shadow-sm border-0 mb-4">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="px-4 py-3 border-0">Sl No</th>
                                    <th className="px-4 py-3 border-0">Client Name</th>
                                    <th className="px-4 py-3 border-0">Type</th>
                                    <th className="px-4 py-3 border-0 text-end">Closing Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5">
                                            <div className="spinner-border text-primary" role="status">
                                                <span className="visually-hidden">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted">Calculating balances...</p>
                                        </td>
                                    </tr>
                                ) : error ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5 text-danger">
                                            <i className="bi bi-exclamation-triangle fs-1"></i>
                                            <p className="mt-2">{error}</p>
                                            <button className="btn btn-outline-danger btn-sm" onClick={fetchBalances}>Retry</button>
                                        </td>
                                    </tr>
                                ) : data.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="text-center py-5 text-muted">
                                            <i className="bi bi-inbox fs-1"></i>
                                            <p className="mt-2 text-muted">No balances found for the selected month.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="px-4 py-3 align-middle">{((currentPage - 1) * pagination.limit) + index + 1}</td>
                                            <td className="px-4 py-3 align-middle fw-bold text-dark">{item.name}</td>
                                            <td className="px-4 py-3 align-middle">
                                                <span className={`badge ${item.type === 'Customer' ? 'bg-primary-subtle text-primary' : 'bg-success-subtle text-success'} border-0`}>
                                                    {item.type}
                                                </span>
                                            </td>
                                            <td className={`px-4 py-3 align-middle text-end fw-bold ${item.balance > 0 ? 'text-danger' : 'text-success'}`}>
                                                {formatCurrency(item.balance)}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                            {!loading && !error && data.length > 0 && (
                                <tfoot className="bg-light fw-bold">
                                    <tr>
                                        <td colSpan="3" className="px-4 py-3 text-end">Page Total</td>
                                        <td className="px-4 py-3 text-end text-primary">
                                            {formatCurrency(data.reduce((sum, item) => sum + item.balance, 0))}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan="3" className="px-4 py-3 text-end text-muted small">Grand Total (All Pages)</td>
                                        <td className="px-4 py-3 text-end text-muted small">
                                            {formatCurrency(statistics.totalCustomerBalance + statistics.totalVendorBalance)}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>
            </div>

            {/* Pagination Controls */}
            {renderPagination()}

            {/* Pagination Info */}
            {!loading && !error && data.length > 0 && (
                <div className="text-center mt-2">
                    <Badge bg="light" text="dark" className="px-3 py-2">
                        Showing {data.length} of {pagination.total} results
                    </Badge>
                </div>
            )}
        </div>
    );
};

export default MonthWiseBalanceReport;
